import { KeyRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Field } from "../../admin/ui.tsx";
import type { SessionUser } from "../../admin/session.ts";
import { useSessionActions } from "../../admin/session.ts";
import { signInWithPassword } from "../../auth/auth-api.ts";
import { passkeysSupported, signInWithPasskey } from "../../auth/diver-auth.ts";
import { Button } from "../../components/Button.tsx";
import { failureText, looksLikeEmail } from "./messages.ts";
import { PasswordField } from "./PasswordField.tsx";

interface Props {
  locale: string;
  /** Called with the signed-in account once the session is established. */
  onSignedIn: (user: SessionUser | null) => void;
  /** `?next=` and similar, appended to the links to the other auth pages. */
  search?: string;
  /** False while registration is closed: no "Konto erstellen" link. */
  registrationOpen: boolean;
}

/**
 * The one sign-in form (diver and staff): passkey on top when WebAuthn exists, then email +
 * password, then the links. One message for unknown email and wrong password.
 */
export function SignInForm({ locale, onSignedIn, search = "", registrationOpen }: Props) {
  const { t } = useTranslation();
  const actions = useSessionActions();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError(null);
    if (!email.trim() || !password) return setError(t("diver.login.missing"));
    if (!looksLikeEmail(email)) return setError(t("diver.login.badEmail"));
    setBusy(true);
    const res = await signInWithPassword(email.trim(), password, locale);
    if (!res.ok) {
      setBusy(false);
      return setError(failureText(t, res));
    }
    try {
      onSignedIn(await actions.afterSignIn()); // the session appears and the page moves on
    } finally {
      setBusy(false);
    }
  };

  const passkey = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await signInWithPasskey();
      if (res.ok) onSignedIn(await actions.afterSignIn());
      else setError(failureText(t, { ok: false, reason: res.reason ?? "failed" }));
    } catch {
      setError(failureText(t, { ok: false, reason: "failed" }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="grid gap-3"
      noValidate
      aria-label={t("diver.login.title")}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {passkeysSupported() ? (
        <>
          <Button
            variant="tinted"
            disabled={busy}
            icon={<KeyRound aria-hidden="true" strokeWidth={1.75} />}
            onClick={() => void passkey()}
          >
            {t("diver.login.passkey")}
          </Button>
          <p className="login-or ds-text-footnote text-label-secondary m-0" aria-hidden="true">
            {t("diver.login.or")}
          </p>
        </>
      ) : null}
      <Field label={t("diver.login.email")}>
        {({ id }) => (
          <input
            id={id}
            name="email"
            className="adm-input"
            type="email"
            autoComplete="email webauthn"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        )}
      </Field>
      <PasswordField
        label={t("diver.login.password")}
        autoComplete="current-password"
        value={password}
        onChange={setPassword}
      />
      {error ? (
        <p className="adm-error m-0" role="alert">
          {error}
        </p>
      ) : null}
      <Button type="submit" variant="filled" disabled={busy}>
        {t("diver.login.submit")}
      </Button>
      <p className="login-links ds-text-subheadline m-0">
        <Link className="ds-btn ds-btn--plain" to={`/${locale}/login/passwort-vergessen${search}`}>
          {t("diver.login.forgot")}
        </Link>
        {registrationOpen ? (
          <Link className="ds-btn ds-btn--plain" to={`/${locale}/login/registrieren${search}`}>
            {t("diver.login.toRegister")}
          </Link>
        ) : null}
      </p>
    </form>
  );
}
